import 'video.js/dist/video-js.css';
import 'videojs-hls-quality-selector';
import 'videojs-hotkeys';

import {useEffect, useRef, useState} from 'react';
import videojs from 'video.js';

import {DEFAULT_VIDEO_PLAYER_TIME_STEP} from '../WorkspaceDrawer/Settings/media/components/VideoPlayerTimeStep';
import {DownloadButton, downloadPlugin} from './DownloadButtonPlugin';
import {handleVideoEvents} from './handleVideoEvents';
import {
  type VideoJsPlayerWithQualitySelector,
  type VideoStreamingPlayerProps,
} from './types';

type UseInitializeVideoPlayerProps = Pick<
  VideoStreamingPlayerProps,
  | 'hlsSrc'
  | 'downloadUrl'
  | 'videoFilename'
  | 'trackSuccess'
  | 'trackError'
  | 'setHasFailedToLoadStream'
  | 'onPlaybackFailed'
  | 'autoPlay'
  | 'autoLoop'
  | 'mimeType'
  | 'videoPlayerTimeStep'
>;

videojs.registerComponent('downloadButton', DownloadButton);
videojs.registerPlugin('downloadPlugin', downloadPlugin);

export function useInitializeVideoPlayer({
  hlsSrc,
  downloadUrl,
  trackSuccess,
  trackError,
  setHasFailedToLoadStream,
  onPlaybackFailed,
  autoPlay,
  autoLoop,
  mimeType,
  videoPlayerTimeStep,
}: UseInitializeVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<VideoJsPlayerWithQualitySelector | null>(null);
  // The video's decoded pixel dimensions, captured once metadata loads.
  const [decodedDimensions, setDecodedDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Store as a ref so that the current download url is available to the DownloadButton component
  const downloadUrlRef = useRef<string | null>(downloadUrl);
  // oxlint-disable-next-line react/refs
  downloadUrlRef.current = downloadUrl;

  useEffect(() => {
    // Initialize Video.js player
    if (videoRef.current && !playerRef.current) {
      const videoElement = videoRef.current;
      const sources = hlsSrc
        ? [
            {
              src: hlsSrc,
              type: 'application/x-mpegURL',
              withCredentials: true,
            },
          ]
        : [
            {
              src: downloadUrl,
              type: mimeType,
              withCredentials: true,
            },
          ];

      playerRef.current = videojs(
        videoElement,
        {
          autoplay: autoPlay ?? false,
          muted: true,
          controls: true,
          controlBar: {
            children: [
              'playToggle',
              'currentTimeDisplay',
              'timeDivider',
              'durationDisplay',
              'progressControl',
              'volumePanel',
              'playbackRateMenuButton',
              'fullscreenToggle',
            ],
          },
          loop: autoLoop ?? false,
          fill: true,
          playbackRates: [0.5, 1, 1.5, 2],
          playsInline: true,
          preload: 'auto',
          html5: {
            vhs: {
              withCredentials: true,
            },
          },
          sources,
          responsive: true,
          breakpoints: {
            tiny: 100,
            xsmall: 200,
          },
          plugins: {
            hotkeys: {
              volumeStep: 0.1,
              seekStep: videoPlayerTimeStep ?? DEFAULT_VIDEO_PLAYER_TIME_STEP,
              enableModifiersForNumbers: false,
            },
          },
        },
        () => {
          // See https://github.com/chrisboustead/videojs-hls-quality-selector/issues/79 and other similar
          // issues. TL;DR: Safari doesn't have any API for getting the quality levels. So we can just hide the
          // quality selector plugin.
          if (!videojs.browser.IS_SAFARI && hlsSrc != null) {
            // enable the quality selector plugin
            playerRef.current?.hlsQualitySelector({
              displayCurrentQuality: true,
            });
            // NOTE: It's a known issue that the quality selector doesn't happen instantaneously, because
            // the plugin doesn't flush the buffer of existing chunks at the previous quality.
            // See https://github.com/chrisboustead/videojs-hls-quality-selector/issues/114
            // There aren't any better available plugins I could find that would work better, so
            // the only alternative would be to create a custom quality selector. I think this is fine for now.
          }
        }
      ) as VideoJsPlayerWithQualitySelector;
      handleVideoEvents({
        player: playerRef.current,
        trackSuccess,
        trackError,
        setHasFailedToLoadStream,
        onPlaybackFailed,
        hlsSrc,
        downloadUrl,
        mimeType: mimeType,
        onDecodedDimensions: dimensions =>
          setDecodedDimensions(prev =>
            prev != null &&
            prev.width === dimensions.width &&
            prev.height === dimensions.height
              ? prev
              : dimensions
          ),
      });

      playerRef.current.downloadPlugin({downloadUrlRef});

      // videojs-hotkeys calls preventDefault on arrow keys but not stopPropagation,
      // so they bubble to the panel navigation handler.
      playerRef.current.on('keydown', (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.stopPropagation();
        }
      });
    }
  }, [
    hlsSrc,
    trackSuccess,
    trackError,
    setHasFailedToLoadStream,
    onPlaybackFailed,
    downloadUrl,
    mimeType,
    autoPlay,
    autoLoop,
    videoPlayerTimeStep,
  ]);

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    if (hlsSrc) {
      playerRef.current.src({
        src: hlsSrc,
        type: 'application/x-mpegURL',
      });
    } else {
      playerRef.current.src({
        src: downloadUrl,
        type: mimeType,
      });
    }
  }, [hlsSrc, downloadUrl, mimeType]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return {videoRef, playerRef, decodedDimensions};
}
